import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCalendarEvent,
  IconCheck,
  IconClock,
  IconMapPin,
  IconPhone,
  IconToolsKitchen2,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useContactForm } from '@/hooks/useContactForm';

const thursdayDates = [
  'February 19',
  'February 26',
  'March 5',
  'March 12',
  'March 19',
];

export function LunchOnUsPage() {
  const { submitForm, isSubmitting } = useContactForm();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    initialValues: {
      name: '',
      title: '',
      organization: '',
      email: '',
      phone: '',
      dates: [] as string[],
    },
    validate: {
      name: (value) => (value.trim().length < 2 ? 'Name is required' : null),
      organization: (value) =>
        value.trim().length < 2 ? 'Organization is required' : null,
      email: (value, values) =>
        value.trim() === '' && values.phone.trim() === ''
          ? 'Email or phone is required'
          : value.trim() !== '' && !/^\S+@\S+$/.test(value)
            ? 'Invalid email'
            : null,
      phone: (value, values) =>
        value.trim() === '' && values.email.trim() === ''
          ? 'Email or phone is required'
          : null,
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    const lines = [
      `Title: ${values.title || 'N/A'}`,
      `Organization: ${values.organization}`,
      values.dates.length > 0
        ? `Preferred date(s): ${values.dates.join(', ')}`
        : 'No date preference selected',
    ];

    const result = await submitForm({
      name: values.name,
      email: values.email || 'not-provided@placeholder.com',
      phone: values.phone,
      interest: 'lunch-on-us',
      message: lines.join('\n'),
    });

    if (result.success) {
      setSubmitted(true);
      form.reset();
      notifications.show({
        title: "You're all set!",
        message: "We'll have your egg rolls ready. See you Thursday!",
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } else {
      notifications.show({
        title: 'Error',
        message:
          result.error ||
          'Something went wrong. Please try again or call (651) 400-0063.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  return (
    <>
      {/* Hero */}
      <Box
        py={80}
        style={{
          background:
            'linear-gradient(135deg, var(--mantine-color-harmony-8) 0%, var(--mantine-color-harmony-6) 100%)',
        }}
      >
        <Container size="md">
          <Stack align="center" gap="md">
            <Text c="white" fw={600} tt="uppercase" size="sm" style={{ letterSpacing: 2 }}>
              Hillside Harmony & Culturally Conscious Kitchen
            </Text>
            <Title c="white" ta="center" fz={{ base: 32, sm: 42 }}>
              Care Coordinators — Lunch Is On Us!
            </Title>
            <Text c="rgba(255,255,255,0.85)" ta="center" size="lg" maw={550}>
              Come visit our newly opened assisted living community. See how we
              support the families you serve with compassionate, culturally
              responsive care.
            </Text>
            <Text c="white" ta="center" size="lg" fw={500} mt="sm">
              Swing by during your lunch break and enjoy a personal tray of egg
              rolls — our treat.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Details + Form */}
      <Box py={60}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={50}>
            {/* Left: Details */}
            <Stack gap="xl">
              <Box>
                <Title order={2} mb="md">
                  What to Expect
                </Title>
                <Text c="dimmed" size="lg">
                  Tour our community, meet our team, and see firsthand the
                  culturally responsive environment we've built for the families
                  you serve. Plus, take home a personal tray of egg rolls
                  courtesy of Culturally Conscious Kitchen.
                </Text>
              </Box>

              <Stack gap="lg">
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size={50} radius="md" variant="light" color="harmony">
                    <IconCalendarEvent size={24} stroke={1.5} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={500}>When</Text>
                    <Text c="dimmed">
                      Thursdays | 11:00 AM – 1:00 PM
                    </Text>
                    <Text c="dimmed" size="sm">
                      February 19 – March 19
                    </Text>
                  </Box>
                </Group>

                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size={50} radius="md" variant="light" color="harmony">
                    <IconMapPin size={24} stroke={1.5} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={500}>Where</Text>
                    <Text
                      component="a"
                      href="https://maps.google.com/?q=135+Geranium+Ave+E,+Saint+Paul,+MN+55117"
                      target="_blank"
                      c="harmony.6"
                      td="none"
                    >
                      135 Geranium Ave E, Saint Paul, MN 55117
                    </Text>
                  </Box>
                </Group>

                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size={50} radius="md" variant="light" color="harmony">
                    <IconToolsKitchen2 size={24} stroke={1.5} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={500}>The Menu</Text>
                    <Text c="dimmed">
                      A personal tray of egg rolls — courtesy of Culturally
                      Conscious Kitchen
                    </Text>
                  </Box>
                </Group>

                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size={50} radius="md" variant="light" color="harmony">
                    <IconPhone size={24} stroke={1.5} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={500}>Questions?</Text>
                    <Text
                      component="a"
                      href="tel:+16514000063"
                      c="harmony.6"
                      td="none"
                    >
                      (651) 400-0063
                    </Text>
                    <Text size="sm" c="dimmed">
                      info@hillsideharmony.com
                    </Text>
                  </Box>
                </Group>

                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size={50} radius="md" variant="light" color="harmony">
                    <IconClock size={24} stroke={1.5} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={500}>No RSVP Required</Text>
                    <Text c="dimmed">
                      Walk-ins welcome! But letting us know helps us have your
                      tray ready.
                    </Text>
                  </Box>
                </Group>
              </Stack>
            </Stack>

            {/* Right: RSVP Form */}
            <Card padding="xl" shadow="sm" radius="lg">
              <Title order={3} mb="xs">
                Let Us Know You're Coming
              </Title>
              <Text c="dimmed" mb="lg" size="sm">
                So we can have your egg rolls ready when you arrive.
              </Text>

              {submitted ? (
                <Alert
                  icon={<IconCheck size={16} />}
                  title="You're all set!"
                  color="green"
                  variant="light"
                >
                  We'll have your egg rolls ready. See you Thursday at Hillside
                  Harmony!
                </Alert>
              ) : (
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Stack gap="md">
                    <TextInput
                      label="Your Name"
                      placeholder="Jane Smith"
                      required
                      {...form.getInputProps('name')}
                    />
                    <TextInput
                      label="Title"
                      placeholder="Care Coordinator"
                      {...form.getInputProps('title')}
                    />
                    <TextInput
                      label="Organization"
                      placeholder="Ramsey County, Blue Plus, etc."
                      required
                      {...form.getInputProps('organization')}
                    />
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <TextInput
                        label="Email"
                        placeholder="jane@example.com"
                        {...form.getInputProps('email')}
                      />
                      <TextInput
                        label="Phone"
                        placeholder="(555) 123-4567"
                        {...form.getInputProps('phone')}
                      />
                    </SimpleGrid>
                    <Text size="xs" c="dimmed" mt={-8}>
                      Email or phone required — so we can confirm your visit.
                    </Text>

                    <Box>
                      <Text fw={500} size="sm" mb="xs">
                        Which Thursday(s) are you planning to stop by?
                      </Text>
                      <Checkbox.Group
                        value={form.values.dates}
                        onChange={(value) => form.setFieldValue('dates', value)}
                      >
                        <Stack gap="xs">
                          {thursdayDates.map((date) => (
                            <Checkbox
                              key={date}
                              value={date}
                              label={`Thursday, ${date}`}
                            />
                          ))}
                        </Stack>
                      </Checkbox.Group>
                    </Box>

                    <Button
                      type="submit"
                      size="lg"
                      fullWidth
                      loading={isSubmitting}
                      mt="sm"
                    >
                      Save My Egg Rolls
                    </Button>
                  </Stack>
                </form>
              )}
            </Card>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Map */}
      <Box pb={60}>
        <Container size="xl">
          <Card padding={0} radius="lg" h={300} style={{ overflow: 'hidden' }}>
            <iframe
              title="Hillside Harmony Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2821.137!2d-93.0593!3d44.9968!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b32d144b1e1d3b%3A0x8c9d0e1f2a3b4c5d!2s135%20Geranium%20Ave%20E%2C%20Saint%20Paul%2C%20MN%2055117!5e0!3m2!1sen!2sus!4v1648000000000"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
            />
          </Card>
        </Container>
      </Box>
    </>
  );
}
